import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useRef, useEffect } from 'preact/hooks';
import { ArrowUp } from 'lucide-preact';
export function WebsterChatInput({ onSend, disabled, buttonLabel, allowEmpty = false, placeholder = 'Type a message...', lockedByUserName, }) {
    const [message, setMessage] = useState('');
    const textareaRef = useRef(null);
    // Effective disable: own turn (disabled) OR another user's turn (lockedByUserName).
    const lockedByOther = !!lockedByUserName;
    const effectiveDisabled = disabled || lockedByOther;
    const effectivePlaceholder = lockedByOther
        ? `${lockedByUserName} is asking Webster…`
        : (disabled ? 'Webster is working...' : placeholder);
    const canSubmit = !effectiveDisabled && (allowEmpty || message.trim().length > 0);
    useEffect(() => {
        if (!effectiveDisabled && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [effectiveDisabled]);
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSubmit)
            return;
        onSend(message.trim());
        setMessage('');
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };
    const handleInput = (e) => {
        const target = e.target;
        setMessage(target.value);
        target.style.height = 'auto';
        target.style.height = `${Math.min(target.scrollHeight, 160)}px`;
    };
    return (_jsx("div", { className: "px-4 py-3 bg-white dark:bg-slate-900", children: _jsxs("div", { className: "max-w-3xl mx-auto", children: [_jsxs("div", { className: `
                        relative rounded-2xl border transition-colors
                        ${effectiveDisabled
                        ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'}
                    `, children: [_jsx("textarea", { ref: textareaRef, value: message, onInput: handleInput, onKeyDown: handleKeyDown, placeholder: effectivePlaceholder, disabled: effectiveDisabled, rows: 1, className: "w-full px-4 pt-3 pb-10 bg-transparent text-slate-900 dark:text-slate-100\n                            focus:outline-none resize-none disabled:text-slate-400\n                            text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500", style: { minHeight: '52px', maxHeight: '160px' } }), _jsxs("div", { className: "absolute bottom-2 right-2 flex items-center gap-2", children: [buttonLabel && (_jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500 mr-1", children: buttonLabel })), _jsx("button", { type: "button", onClick: handleSubmit, disabled: !canSubmit, className: `
                                p-1.5 rounded-lg transition-colors
                                ${canSubmit
                                        ? 'bg-primary-600 hover:bg-primary-700 text-white'
                                        : 'bg-slate-200 dark:bg-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed'}
                            `, "aria-label": "Send message", children: effectiveDisabled ? (_jsx("div", { className: "w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" })) : (_jsx(ArrowUp, { className: "w-4 h-4" })) })] })] }), _jsx("p", { className: "mt-1.5 text-xs text-center text-slate-400 dark:text-slate-500", children: "Press Enter to send \u00B7 Shift+Enter for new line" })] }) }));
}
